import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Typography, 
  Row, 
  Col, 
  Statistic, 
  DatePicker, 
  Select, 
  message,
  Modal,
  List,
  Avatar,
  Badge,
  Tooltip
} from 'antd';
import { 
  CalendarOutlined, 
  ClockCircleOutlined, 
  EnvironmentOutlined, 
  UserOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  PlusOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { AuthService } from '../../services/authService';

dayjs.locale('vi');

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface AttendanceSession {
  SessionID: number;
  SessionDate: string;
  SessionTime: string;
  Location: string;
  StartTime: string;
  EndTime: string;
  Status: string;
  TotalPresent: number;
  OpenedAt: string;
  ClosedAt: string;
  CreatedAt: string;
}

interface Attendee {
  STT: number;
  Username: string;
  FullName: string;
  RoleName: string;
  Department: string;
  CheckInTime: string;
}

const DailyAttendanceManagementPage: React.FC = () => {
  const [sessions, setSessions] = useState<AttendanceSession[]>([]);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState<AttendanceSession | null>(null);
  const [attendeesModalVisible, setAttendeesModalVisible] = useState(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs()
  ]);
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchSessions();
  }, [dateRange, locationFilter, statusFilter]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        location: locationFilter,
        status: statusFilter
      });

      const response = await fetch(`/api/v1/daily-attendance/sessions?${params}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setSessions(result.data || []);
      } else {
        message.error(result.error || 'Lỗi khi tải dữ liệu');
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
      message.error('Lỗi kết nối đến server');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendees = async (sessionId: number) => {
    try {
      const response = await fetch(`/api/v1/daily-attendance/${sessionId}/attendees`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setAttendees(result.data || []);
      } else {
        message.error(result.error || 'Lỗi khi tải danh sách điểm danh');
      }
    } catch (error) {
      console.error('Error fetching attendees:', error);
      message.error('Lỗi kết nối đến server');
    }
  };

  const handleViewAttendees = async (session: AttendanceSession) => {
    setSelectedSession(session);
    setAttendeesModalVisible(true);
    await fetchAttendees(session.SessionID);
  };

  const handleManualOpen = async (sessionId: number) => {
    try {
      const response = await fetch(`/api/v1/daily-attendance/${sessionId}/open`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        message.success('Đã mở phiên điểm danh');
        fetchSessions();
      } else {
        message.error(result.error || 'Lỗi khi mở phiên');
      }
    } catch (error) {
      console.error('Error opening session:', error);
      message.error('Lỗi kết nối đến server');
    }
  };

  const handleManualClose = async (sessionId: number) => {
    try {
      const response = await fetch(`/api/v1/daily-attendance/${sessionId}/close`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        message.success('Đã đóng phiên điểm danh');
        fetchSessions();
      } else {
        message.error(result.error || 'Lỗi khi đóng phiên');
      }
    } catch (error) {
      console.error('Error closing session:', error);
      message.error('Lỗi kết nối đến server');
    }
  };

  const handleCreateWeeklySessions = async (location: 'hospital' | 'school') => {
    Modal.confirm({
      title: `Tạo phiên điểm danh tuần này cho ${location === 'hospital' ? 'Bệnh viện' : 'Trường'}?`,
      content: (
        <div>
          <p>Hệ thống sẽ tự động tạo phiên điểm danh cho tuần này (Thứ 2 - Thứ 6):</p>
          <ul>
            <li>4 buổi mỗi ngày (sáng bắt đầu, sáng kết thúc, chiều bắt đầu, chiều kết thúc)</li>
            <li>Tổng cộng: <strong>20 phiên</strong> (4 buổi × 5 ngày)</li>
            <li>Các phiên đã tồn tại sẽ được bỏ qua</li>
          </ul>
        </div>
      ),
      okText: 'Tạo phiên',
      cancelText: 'Hủy',
      width: 500,
      onOk: async () => {
        try {
          // Get Monday and Friday of current week
          const today = dayjs();
          const monday = today.startOf('week').add(1, 'day'); // Monday
          const friday = monday.add(4, 'day'); // Friday

          const response = await fetch('/api/v1/daily-attendance/create-weekly-sessions', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${AuthService.getToken()}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              startDate: monday.format('YYYY-MM-DD'),
              endDate: friday.format('YYYY-MM-DD'),
              location
            })
          });

          const result = await response.json();
          
          if (result.success) {
            message.success(`Đã tạo ${result.data?.created || 0} phiên mới cho ${location === 'hospital' ? 'Bệnh viện' : 'Trường'}`);
            fetchSessions();
          } else {
            message.error(result.error || 'Lỗi khi tạo phiên');
          }
        } catch (error) {
          console.error('Error creating weekly sessions:', error);
          message.error('Lỗi kết nối đến server');
        }
      }
    });
  };

  const getLocationName = (location: string) => {
    switch (location) {
      case 'hospital': return 'Bệnh viện';
      case 'school': return 'Trường';
      default: return location;
    }
  };

  const getSessionTimeName = (time: string) => {
    switch (time) {
      case 'morning_start': return 'Đầu giờ sáng';
      case 'morning_end': return 'Cuối giờ sáng';
      case 'afternoon_start': return 'Đầu giờ chiều';
      case 'afternoon_end': return 'Cuối giờ chiều';
      default: return time;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'default';
      case 'open': return 'processing';
      case 'closed': return 'success';
      case 'auto_closed': return 'warning';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'scheduled': return 'Đã lên lịch';
      case 'open': return 'Đang mở';
      case 'closed': return 'Đã đóng';
      case 'auto_closed': return 'Tự động đóng';
      default: return status;
    }
  };

  const columns = [
    {
      title: 'Ngày',
      dataIndex: 'SessionDate',
      key: 'SessionDate',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      sorter: (a: AttendanceSession, b: AttendanceSession) => 
        dayjs(a.SessionDate).unix() - dayjs(b.SessionDate).unix(),
    },
    {
      title: 'Buổi',
      dataIndex: 'SessionTime',
      key: 'SessionTime',
      render: (time: string) => getSessionTimeName(time),
    },
    {
      title: 'Địa điểm',
      dataIndex: 'Location',
      key: 'Location',
      render: (location: string) => getLocationName(location),
      filters: [
        { text: 'Bệnh viện', value: 'hospital' },
        { text: 'Trường', value: 'school' },
      ],
      onFilter: (value: any, record: AttendanceSession) => record.Location === value,
    },
    {
      title: 'Thời gian',
      key: 'time',
      render: (record: AttendanceSession) => (
        <div>
          <div>
            {/* ✅ FIX: Display TIME string directly without timezone conversion */}
            {record.StartTime} - {record.EndTime}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.OpenedAt ? `Mở: ${dayjs(record.OpenedAt).format('HH:mm')}` : ''}
            {record.ClosedAt ? ` | Đóng: ${dayjs(record.ClosedAt).format('HH:mm')}` : ''}
          </Text>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'Status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
      filters: [
        { text: 'Đã lên lịch', value: 'scheduled' },
        { text: 'Đang mở', value: 'open' },
        { text: 'Đã đóng', value: 'closed' },
        { text: 'Tự động đóng', value: 'auto_closed' },
      ],
      onFilter: (value: any, record: AttendanceSession) => record.Status === value,
    },
    {
      title: 'Số người',
      dataIndex: 'TotalPresent',
      key: 'TotalPresent',
      render: (count: number) => (
        <Badge count={count} style={{ backgroundColor: '#52c41a' }} />
      ),
      sorter: (a: AttendanceSession, b: AttendanceSession) => a.TotalPresent - b.TotalPresent,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (record: AttendanceSession) => (
        <Space>
          <Tooltip title="Xem danh sách điểm danh">
            <Button 
              type="primary" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => handleViewAttendees(record)}
            >
              Xem
            </Button>
          </Tooltip>
          
          {record.Status === 'scheduled' && (
            <Tooltip title="Mở phiên thủ công">
              <Button 
                type="primary" 
                icon={<PlayCircleOutlined />} 
                size="small"
                onClick={() => handleManualOpen(record.SessionID)}
              >
                Mở
              </Button>
            </Tooltip>
          )}
          
          {record.Status === 'open' && (
            <Tooltip title="Đóng phiên thủ công">
              <Button 
                danger 
                icon={<PauseCircleOutlined />} 
                size="small"
                onClick={() => handleManualClose(record.SessionID)}
              >
                Đóng
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Calculate statistics
  const totalSessions = sessions.length;
  const openSessions = sessions.filter(s => s.Status === 'open').length;
  const totalAttendances = sessions.reduce((sum, s) => sum + s.TotalPresent, 0);
  const avgAttendance = totalSessions > 0 ? Math.round(totalAttendances / totalSessions) : 0;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Quản lý điểm danh cán bộ hàng ngày</Title>
        <Text type="secondary">
          Quản lý và theo dõi phiên điểm danh tự động cho cán bộ/giảng viên
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Tổng phiên"
              value={totalSessions}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Đang mở"
              value={openSessions}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Tổng lượt điểm danh"
              value={totalAttendances}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Trung bình/phiên"
              value={avgAttendance}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Action Buttons */}
      <Card style={{ marginBottom: 16 }}>
        <Space size="middle">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleCreateWeeklySessions('hospital')}
          >
            Tạo phiên tuần (Bệnh viện)
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleCreateWeeklySessions('school')}
            style={{ background: '#52c41a', borderColor: '#52c41a' }}
          >
            Tạo phiên tuần (Trường)
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchSessions}
          >
            Làm mới
          </Button>
        </Space>
      </Card>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Text strong>Lọc theo:</Text>
          </Col>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col>
            <Select
              value={locationFilter}
              onChange={setLocationFilter}
              style={{ width: 120 }}
            >
              <Option value="all">Tất cả địa điểm</Option>
              <Option value="hospital">Bệnh viện</Option>
              <Option value="school">Trường</Option>
            </Select>
          </Col>
          <Col>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
            >
              <Option value="all">Tất cả trạng thái</Option>
              <Option value="scheduled">Đã lên lịch</Option>
              <Option value="open">Đang mở</Option>
              <Option value="closed">Đã đóng</Option>
              <Option value="auto_closed">Tự động đóng</Option>
            </Select>
          </Col>
          <Col>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchSessions}
              loading={loading}
            >
              Làm mới
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Sessions Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={sessions}
          rowKey="SessionID"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} của ${total} phiên`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Attendees Modal */}
      <Modal
        title={`Danh sách điểm danh - ${selectedSession ? getSessionTimeName(selectedSession.SessionTime) : ''}`}
        open={attendeesModalVisible}
        onCancel={() => setAttendeesModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedSession && (
          <div style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Text strong>Ngày: </Text>
                {dayjs(selectedSession.SessionDate).format('DD/MM/YYYY')}
              </Col>
              <Col span={8}>
                <Text strong>Địa điểm: </Text>
                {getLocationName(selectedSession.Location)}
              </Col>
              <Col span={8}>
                <Text strong>Thời gian: </Text>
                {/* ✅ FIX: Display TIME string directly without timezone conversion */}
                {selectedSession.StartTime} - {selectedSession.EndTime}
              </Col>
            </Row>
          </div>
        )}

        <List
          dataSource={attendees}
          renderItem={(attendee) => (
            <List.Item>
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={attendee.FullName}
                description={
                  <div>
                    <div>{attendee.RoleName} - {attendee.Department}</div>
                    <Text type="secondary">
                      Điểm danh lúc: {dayjs(attendee.CheckInTime).format('HH:mm:ss')}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
};

export default DailyAttendanceManagementPage;
